// Notification levels: shared so the room (who gets a push) and the web app (the bell menu) agree on defaults.
import type { Channel, NotifyLevel } from "./protocol.ts";

export const NOTIFY_LEVELS: readonly NotifyLevel[] = ["all", "mentions", "none"];

/** DMs notify for every message; channels only when you're @mentioned (like Discord's default for big servers). */
export const defaultNotifyLevel = (kind: Channel["kind"] | undefined): NotifyLevel => (kind === "dm" ? "all" : "mentions");

export const isNotifyLevel = (v: unknown): v is NotifyLevel => typeof v === "string" && (NOTIFY_LEVELS as readonly string[]).includes(v);

// ---- Server-level settings and mutes (Discord's model) ----
//
// What notifies you about a conversation, strongest first:
//   1. This device: push off here (or iOS said no) → nothing on this device, whatever the settings below say.
//   2. Level "none" (the channel's, else the server's) → nothing.
//   3. Muted (the channel or DM for a while or for good; or the whole server, which never covers DMs) → only
//      @mentions get through, and the conversation hides its unread dot (mention badges stay).
//   4. The level: the channel's own if you picked one, otherwise the server's (text channels; DMs default to "all"),
//      otherwise "mentions".
//   "Suppress @everyone" (per server) makes @everyone count as a plain message for you in that server's channels.

/** How long a mute lasts. "forever" is until you unmute. */
export type MuteFor = "24h" | "1w" | "forever";
export const MUTE_OPTIONS: readonly { for: MuteFor; label: string; ms: number | null }[] = [
  { for: "24h", label: "For 24 hours", ms: 24 * 3600_000 },
  { for: "1w", label: "For 1 week", ms: 7 * 24 * 3600_000 },
  { for: "forever", label: "Until I turn it back on", ms: null },
];
/** A mute's end for "forever". */
export const MUTE_FOREVER = -1;
export const isMuteFor = (v: unknown): v is MuteFor => v === "24h" || v === "1w" || v === "forever";
/** When a mute that starts now ends (ms), or MUTE_FOREVER. */
export function muteUntil(f: MuteFor, now = Date.now()): number {
  const ms = MUTE_OPTIONS.find((o) => o.for === f)!.ms;
  return ms === null ? MUTE_FOREVER : now + ms;
}
/** Is a mute ending at `until` still on? */
export const muteActive = (until: number | null | undefined, now = Date.now()): boolean => until === MUTE_FOREVER || (typeof until === "number" && until > now);

/** My settings for a whole server. */
export interface ServerNotify {
  /** The level for its text channels where I didn't pick one. Missing: "mentions". */
  level?: NotifyLevel;
  /** @everyone counts as a plain message for me here. */
  suppressEveryone?: boolean;
  /** The server is muted until then (ms) or MUTE_FOREVER. Missing: not muted. */
  mutedUntil?: number;
}

/** The level that applies to a conversation: mine for it, else (text channels) the server's, else the kind's default. */
export function effectiveNotifyLevel(kind: Channel["kind"] | undefined, own: NotifyLevel | null | undefined, server: ServerNotify | undefined): NotifyLevel {
  if (own) return own;
  if (kind === "dm") return "all";
  return server?.level ?? "mentions";
}

/** Is this conversation muted for me now? A server mute covers its text channels, never DMs. */
export function isConversationMuted(kind: Channel["kind"] | undefined, ownUntil: number | undefined, server: ServerNotify | undefined, now = Date.now()): boolean {
  return muteActive(ownUntil, now) || (kind !== "dm" && muteActive(server?.mutedUntil, now));
}

/** The next moment any of these mutes ends (to redraw then), or null. */
export function nextMuteEnd(untils: (number | undefined)[], now = Date.now()): number | null {
  let next: number | null = null;
  for (const u of untils) if (typeof u === "number" && u > now && (next === null || u < next)) next = u;
  return next;
}

/** "Muted until Sat 3:00 PM" / "Muted" for a mute that's on. */
export function muteLabel(until: number, now = Date.now(), locale?: string): string {
  if (until === MUTE_FOREVER) return "Muted";
  const d = new Date(until);
  const soon = until - now < 6 * 24 * 3600_000;
  const when = soon
    ? d.toLocaleString(locale, { weekday: "short", hour: "numeric", minute: "2-digit" })
    : d.toLocaleString(locale, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return `Muted until ${when}`;
}
